import { useMessage } from "@/common/messages";
import {
	TransferStartResponse,
	TransferType,
	UnsupportedContactType,
	TransferChannelType,
} from "@/common/types";
import { useMutation } from "@tanstack/react-query";
import { AgentInteractionStatus } from "@/common/constants/callSession";
import { startTransfer } from "@/repository/queryFns/transferToAgent";
import { IntlShape, useIntl } from "react-intl";
import {
	CallSessionActions,
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import {
	transferEmail,
	transferGeneric,
	transferWhatsApp,
	transferWebChat,
} from "@/repository/queryFns";
import { transferVoicemail } from "@/repository/queryFns/voicemails";
import { InitiateTransferRequest } from "@/common/types/transfer";
import { AppCacheState, CacheKey, useAppCacheStore } from "@/common/stores/persistingStores/app";
import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";
import { addBreadcrumb, captureError } from "@/common/helpers/sentryHelper";
import { translateActivity } from "@/common/helpers/presenceHelper";
import { PresenceState } from "@/common/constants/presence";

const buildContactUnavailableText = (
	response: TransferStartResponse,
	intl: IntlShape
): string => {
	const contactUnavailableMessageId = response.isTeamsPresenceDndOverride
		? "Transfer.ErrorMessage.ContactUnavailableTeamsDnd"
		: "Transfer.ErrorMessage.ContactUnavailable";
	const reportedPresenceActivity = response.isTeamsPresenceDndOverride
		? PresenceState.DND
		: response.presenceActivity;
	const presenceStatus = reportedPresenceActivity
		? translateActivity(reportedPresenceActivity, intl)
		: "—";

	return intl.formatMessage({ id: contactUnavailableMessageId }, { presenceStatus });
};

/**
 * The reasons a transfer was refused before it started. Every one of them is shown the same way -- an
 * error in the workspace -- so the caller only needs the text, and null means nothing refused it.
 * Order matters and matches what the backend prioritises.
 */
const resolveTransferFailureText = (
	response: TransferStartResponse,
	intl: IntlShape
): string | null => {
	if (response.invalidCall) {
		return intl.formatMessage({ id: "Transfer.ErrorMessage.InvalidCall" });
	}

	if (response.contactNotFound) {
		return intl.formatMessage({ id: "Transfer.ErrorMessage.ContactNotFound" });
	}

	if (response.contactUnavailable) {
		return buildContactUnavailableText(response, intl);
	}

	if (response.contactNotSupported) {
		return intl.formatMessage({
			id: `Transfer.ErrorMessage.ContactNotSupported.${
				UnsupportedContactType[response.contactNotSupported]
			}`,
		});
	}

	if (response.routeCallDisabled) {
		return intl.formatMessage({ id: "Transfer.ErrorMessage.RouteCallDisabled" });
	}

	if (response.multipleContactsWithSameSip) {
		return intl.formatMessage({ id: "Transfer.ErrorMessage.MultipleContactsWithSameSip" });
	}

	return null;
};

const resolveTransferPerformedMessageId = (transferType: TransferType): string =>
	transferType == TransferType.AgentRouteCallTransfer
		? "Transfer.RouteCallPerformed"
		: "Transfer.ColdTransferPerformed";

export const useTransferHook = (
	channelType: TransferChannelType = TransferChannelType.Voice
): [
	startTransferAction: (
		type: TransferType,
		target: string,
		messagingId?: string,
		onTransferCallback?: () => void
	) => void,
] => {
	const { show: showMessage } = useMessage();
	const intl = useIntl();

	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);

	const actions = useCallSessionStore((actions: CallSessionActions) => actions);

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const transferVoicemailAdapter = (req: InitiateTransferRequest): Promise<TransferStartResponse> =>
		transferVoicemail(req.messagingId ?? '', req.transferTargetString ?? '');

	const transferFnMap = {
		[TransferChannelType.Voice]: startTransfer,
		[TransferChannelType.Generic]: transferGeneric,
		[TransferChannelType.Email]: transferEmail,
		[TransferChannelType.WhatsApp]: transferWhatsApp,
		[TransferChannelType.WebChat]: transferWebChat,
		[TransferChannelType.Voicemail]: transferVoicemailAdapter,
	};

	const startTransferAction = (
		type: TransferType,
		target: string,
		messagingId?: string,
		onTransferCallback?: () => void
	) => {
		if (channelType === TransferChannelType.Voice) {
			const startTransferModel: InitiateTransferRequest = {
				type: type,
				target: target,
				onTransferCallback: onTransferCallback,
			};

			addBreadcrumb("ui_interaction", "Starting transfer", {
				channelType,
				...startTransferModel,
			});
			startTransferMutation.mutate(startTransferModel);
		} else {
			const currentInteractionId = appStoreCache[CacheKey.CurrentInteractionId];
			const startTransferModel: InitiateTransferRequest = {
				messagingId: messagingId ?? currentInteractionId,
				transferTargetString: target,
				transferInitiatorUserId: Lockr.get(StorageKeys.UserId),
				onTransferCallback: onTransferCallback,
			};

			addBreadcrumb("ui_interaction", "Starting transfer", {
				channelType,
				...startTransferModel,
			});
			startTransferMutation.mutate(startTransferModel);
		}
	};

	const startTransferMutation = useMutation({
		mutationFn: transferFnMap[channelType],
		onSuccess: (response: TransferStartResponse, variables: any) => {
			const failureText = resolveTransferFailureText(response, intl);

			if (failureText) {
				showMessage("workspace", { intent: "error", text: failureText });

				return;
			}

			if (response.transferStarted) {
				if (variables.type == TransferType.AgentWarmCallTransfer) {
					actions.update({
						...callSession,
						state: AgentInteractionStatus.WarmTransferInviteSent,
					});
				} else if (channelType === TransferChannelType.Voicemail) {
					showMessage("global", {
						intent: "success",
						text: intl.formatMessage({
							id: "Interaction.Voicemails.TransferSuccess",
						}),
					});
				} else {
					showMessage("workspace", {
						intent: "success",
						text: intl.formatMessage({
							id: resolveTransferPerformedMessageId(variables.type),
						}),
					});
				}
			} else if (response.transferAlreadyInProgress) {
				showMessage("workspace", {
					intent: "info",
					text: intl.formatMessage({
						id: "Transfer.ErrorMessage.TransferAlreadyInProgress",
					}),
				});
			} else {
				showMessage("workspace", {
					intent: "error",
					text: intl.formatMessage({
						id: "Transfer.ErrorMessage.TransferNotStartedFromUnknownReasons",
					}),
				});
			}

			if (variables.onTransferCallback) {
				variables.onTransferCallback();
			}
		},
		onError: (err: any, variables: any) => {
			captureError(err, "Error on starting transfer", undefined, variables);
			showMessage("workspace", {
				intent: "error",
				text:
					variables.type == TransferType.AgentWarmCallTransfer
						? intl.formatMessage({
								id: "Transfer.ErrorMessage.WarmTransferFailed",
							})
						: intl.formatMessage({
								id: "Transfer.ErrorMessage.ColdTransferFailed",
							}),
			});
		},
	});

	return [startTransferAction];
};
